/**
 * Admin user ("owner coach") Firestore sidecar at `users/{uid}`.
 *
 * See cheereval/agents/admin_user_discussion.md for the full shape and rationale.
 * Today this doc holds only `email` (mirrored from Firebase Auth) and `phone`.
 */
import {
  doc,
  Firestore,
  getDoc,
  setDoc,
} from 'firebase/firestore';

import type { User } from 'firebase/auth';

const USERS_COLLECTION = 'users';

/**
 * Lazily creates the `users/{uid}` sidecar doc and keeps `email` in sync with Auth.
 *
 * - First call for a uid: writes `{ email, phone: null }`.
 * - Subsequent calls: only refreshes `email` via merge; never overwrites `phone`
 *   or any future fields that were set through their own UI.
 *
 * Safe to call on every admin action that writes under `users/{uid}`.
 */
export async function ensureUserDoc(
  db: Firestore,
  user: User,
): Promise<void> {
  const ref = doc(db, USERS_COLLECTION, user.uid);
  const snap = await getDoc(ref);
  const email = user.email ?? '';

  if (!snap.exists()) {
    await setDoc(ref, { email, phone: null });
    return;
  }

  const existingEmail =
    typeof snap.data().email === 'string' ? (snap.data().email as string) : '';
  if (existingEmail !== email) {
    await setDoc(ref, { email }, { merge: true });
  }
}
